import { ReactElement } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import BigNumber from 'bignumber.js';

import { LIQUID_STAKING_TOKEN_ADDRESS } from 'modules/api/const';
import { useApprove } from 'modules/api/hooks/useApprove';
import { GuardButton } from 'modules/auth/components/GuardButton';
import { Info } from 'modules/common/components/Info/Info';
import { Summary } from 'modules/common/components/Summary';
import { SummaryItem } from 'modules/common/components/SummaryItem';
import {
  BUY_STAKE_MORE_LINK,
  DEFAULT_DECIMAL_PLACES,
  ONE,
} from 'modules/common/const';
import { useGlobalTranslation } from 'modules/i18n';
import { NumberInputs } from 'modules/liquidStaking/components/TabsDialog/components/NumberInputs/NumberInputs';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';
import { useStakeToken } from 'modules/liquidStaking/hooks/useStakeToken';

import { useStakeForm } from '../../../../hooks/useStakeForm';
import { translation } from './translation';
import { useStyles } from './useStyles';

export interface IStakeFormProps {
  onSubmit: (params: { amount: BigNumber; receiveAmount: BigNumber }) => void;
  isSubmitLoading: boolean;
}

export function StakeForm({
  onSubmit,
  isSubmitLoading,
}: IStakeFormProps): ReactElement {
  const { t, keys } = useGlobalTranslation(translation);
  const { classes } = useStyles();

  const {
    amount,
    balance,
    receiveAmount,
    incomeAmount,
    incomeAmountUSD,
    control,
    errors,
    onMaxClick,
    onSubmit: handleSubmit,
    isSubmitDisabled,
    apy,
  } = useStakeForm();

  const {
    isApproved,
    isDisabled: isApproveDisabled,
    isLoading: isApproveLoading,
    onApprove,
  } = useApprove({
    amount,
    poolAddress: LIQUID_STAKING_TOKEN_ADDRESS,
  });

  const { poolAddress } = useLiquidStakingPool();
  const { name: stakeTokenName, ratio, fee } = useStakeToken(poolAddress);

  return (
    <form
      className={classes.root}
      onSubmit={handleSubmit(amount => {
        onSubmit({ amount: new BigNumber(amount), receiveAmount });
      })}
    >
      <NumberInputs
        balance={balance}
        control={control as unknown as Control<FieldValues>}
        error={!!errors.amount?.message}
        getMoreLink={BUY_STAKE_MORE_LINK}
        helperText={errors.amount?.message}
        inputToken="MAT"
        name="amount"
        placeholder="0"
        receiveControlName="receiveAmount"
        receiveToken="stMAT"
        receiveTooltip={t(keys.receiveTokenTooltip, { token: stakeTokenName })}
        unit={t(keys.tokens.main)}
        variant="filled"
        onMaxClick={onMaxClick}
      />

      <div className={classes.infos}>
        {!incomeAmount.isZero() && fee && (
          <Info>
            {t(
              keys.income,
              {
                value: incomeAmount.decimalPlaces(DEFAULT_DECIMAL_PLACES),
                usdValue:
                  incomeAmountUSD && !incomeAmountUSD.isZero()
                    ? t(keys.unit.$, {
                        value: incomeAmountUSD.decimalPlaces(0).toFormat(),
                      })
                    : '',
                apy: apy.decimalPlaces(2),
                fee,
                token: t(keys.tokens.main),
              },
              true,
            )}
          </Info>
        )}
      </div>

      <Summary className={classes.summary}>
        <SummaryItem
          label={t(keys.exchangeRateLabel)}
          value={t(keys.rateValues, {
            input: t(keys.unit.tokenValue, {
              value: ONE.toFormat(),
              token: t(keys.tokens.main),
            }),
            output: t(keys.unit.tokenValue, {
              value: ratio.decimalPlaces(3).toFormat(),
              token: stakeTokenName,
            }),
          })}
        />
      </Summary>

      {isApproved ? (
        <GuardButton
          disabled={isSubmitDisabled}
          loading={isSubmitLoading}
          size="large"
          sx={theme => ({ marginTop: theme.spacing(6) })}
          type="submit"
          variant="contained"
        >
          {t(keys.stake)}
        </GuardButton>
      ) : (
        <GuardButton
          disabled={isApproveDisabled || isSubmitDisabled}
          loading={isApproveLoading}
          size="large"
          sx={theme => ({ marginTop: theme.spacing(6) })}
          variant="contained"
          onClick={() => onApprove()}
        >
          {t(keys.approve)}
        </GuardButton>
      )}
    </form>
  );
}
